import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Trash2, UploadCloud, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInstagram() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [postUrl, setPostUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/instagram");
      setPhotos(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addByLink = async (e) => {
    e.preventDefault();
    if (!postUrl.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/instagram", { post_url: postUrl.trim() });
      toast.success("Instagram post added to the gallery");
      setPostUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      await api.post("/admin/instagram", { image_url: fullUrl });
      toast.success("Photo added to Instagram gallery");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/instagram/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-instagram">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Social</div>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Instagram gallery</h1>
        <p className="text-white/60 text-sm mt-2">Paste a link to one of your Instagram posts — or upload a photo — and it appears in the "On the gram" section on the homepage.</p>
      </div>

      <form onSubmit={addByLink} className="ufr-card rounded-md p-6 mb-4" data-testid="instagram-link-form">
        <div className="flex items-center gap-2 mb-3">
          <Plus size={16} className="text-[color:var(--ufr-green)]" />
          <div className="text-sm font-bold text-white uppercase tracking-wider">Add from a post link</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="flex-1 bg-black/50 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[color:var(--ufr-green)] outline-none"
            value={postUrl}
            onChange={(e) => setPostUrl(e.target.value)}
            placeholder="https://www.instagram.com/p/ABC123xyz/"
            data-testid="instagram-post-url-input"
          />
          <button type="submit" disabled={adding} className="btn-ufr-primary px-6 py-3 text-sm" data-testid="instagram-add-link-btn">
            {adding ? "Adding…" : "Add post"}
          </button>
        </div>
        <div className="text-[11px] text-white/45 mt-2">Works with posts and reels: instagram.com/p/… or instagram.com/reel/…</div>
      </form>

      <label
        className="ufr-card rounded-md p-8 mb-8 flex flex-col items-center justify-center text-center cursor-pointer border-dashed border-2 border-white/15 hover:border-[color:var(--ufr-green)]/50 transition-colors"
        data-testid="instagram-upload-dropzone"
      >
        <UploadCloud size={28} className="text-[color:var(--ufr-green)]" />
        <div className="font-bold text-white mt-3">{uploading ? "Uploading…" : "Click to upload a photo"}</div>
        <div className="text-xs text-white/50 mt-1">JPG, PNG, WEBP or GIF — up to 10MB</div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} data-testid="instagram-upload-input" />
      </label>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : photos.length === 0 ? (
        <div className="ufr-card rounded-md p-10 text-center">
          <Instagram className="mx-auto text-white/30" size={32} />
          <div className="font-display text-2xl text-white mt-3">No photos yet</div>
          <p className="text-white/60 text-sm mt-2">Upload your first Instagram photo above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="instagram-admin-grid">
          {photos.map((p) => (
            <div key={p.id} className="relative aspect-square rounded-md overflow-hidden border border-white/10 group bg-black" data-testid={`instagram-admin-tile-${p.id}`}>
              {p.shortcode ? (
                <iframe
                  src={`https://www.instagram.com/p/${p.shortcode}/embed/`}
                  title="Instagram post"
                  className="absolute w-full border-0"
                  style={{ height: "calc(100% + 110px)", top: -55 }}
                  scrolling="no"
                  loading="lazy"
                />
              ) : (
                <img src={p.image_url} alt="" className="w-full h-full object-cover" />
              )}
              <button
                onClick={() => remove(p.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white/80 hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                data-testid={`instagram-delete-${p.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
